/**
 * v1 sidebar navigation tree.
 *
 * Single source of truth for the left nav. Sections and items are declared here
 * so that as other screens migrate into the v1 shell (issue #636) the only edit
 * is a `to:` value in this file, not markup inside the sidebar component.
 *
 * Most destinations still point at the existing `/dashboard/*` sections. That is
 * deliberate: v1 ships alongside the current app rather than replacing it, so
 * every nav item lands on the screen that works today.
 */

import {
  FeatherBarChart2,
  FeatherBookOpen,
  FeatherClipboard,
  FeatherDownload,
  FeatherFileText,
  FeatherHome,
  FeatherInbox,
  FeatherLandmark,
  FeatherMessageSquare,
  FeatherPenLine,
  FeatherPieChart,
  FeatherSettings,
  FeatherShield,
  FeatherShoppingCart,
} from "@subframe/core";
import type { ComponentType } from "react";
import type { AccountingBackendGate } from "../../../config/accounting";
import { matchesAccountingBackendGate } from "../../../config/accounting";

/**
 * Icon components accept a className so callers can size and colour them.
 * Matches the shape of the Feather icons exported by @subframe/core.
 */
export type NavIcon = ComponentType<{ className?: string }>;

/** Who is allowed to see a nav item. */
export type NavAudience = "all" | "clientViewOnly" | "adminOnly";

export interface NavItemConfig {
  /** Stable key, also used as the React key. */
  id: string;
  label: string;
  icon: NavIcon;
  /** Router path. Omitted when the destination does not exist yet. */
  to?: string;
  /**
   * Rendered muted and non-interactive. Used for destinations the design calls
   * for but the app does not have a screen for yet.
   */
  disabled?: boolean;
  /**
   * Restricts visibility. `clientViewOnly` items are hidden from staff because
   * the underlying route is gated with `clientOnly` in App.tsx and would bounce
   * a staff user to the "client only" explanation page. `adminOnly` is the same
   * bargain the other way: the route carries `requiredRole="admin"` and would
   * bounce everyone else to `/unauthorized`.
   */
  audience?: NavAudience;
  /**
   * When set, only that accounting backend sees the item. Omit for shared
   * destinations (books, banking, shared settings).
   */
  backend?: AccountingBackendGate;
  /** Extra paths that should also light this item up as active. */
  matchPaths?: string[];
}

export interface NavSectionConfig {
  id: string;
  /** Section heading. Omit for the first, unlabelled group. */
  label?: string;
  items: NavItemConfig[];
}

/** Route the Home item points at. Exported so App.tsx and the shell agree. */
export const V1_HOME_PATH = "/v1";

export const NAV_SECTIONS: NavSectionConfig[] = [
  {
    id: "primary",
    items: [
      { id: "home", label: "Home", icon: FeatherHome, to: V1_HOME_PATH },
      { id: "inbox", label: "Inbox", icon: FeatherInbox, to: "/dashboard/inbox" },
      { id: "data-entry", label: "Data Entry", icon: FeatherPenLine, to: "/dashboard/data" },
      { id: "queries", label: "Queries", icon: FeatherMessageSquare, to: "/dashboard/queries" },
    ],
  },
  {
    id: "books",
    label: "BOOKS",
    items: [
      {
        id: "sales",
        label: "Sales",
        icon: FeatherShoppingCart,
        to: "/dashboard/invoices",
        matchPaths: ["/dashboard/credit"],
      },
      {
        id: "purchases",
        label: "Purchases",
        icon: FeatherFileText,
        to: "/dashboard/bills",
        matchPaths: ["/dashboard/debit"],
      },
      { id: "journals", label: "Journals", icon: FeatherBookOpen, to: "/dashboard/journal" },
      { id: "banking", label: "Banking", icon: FeatherLandmark, to: "/dashboard/cash-bank" },
      {
        id: "tally-connector",
        label: "Tally connector",
        icon: FeatherDownload,
        to: "/tally-connector",
        backend: "tally",
      },
    ],
  },
  {
    id: "compliance",
    label: "COMPLIANCE",
    items: [
      {
        id: "gst-2b",
        label: "GST and 2B",
        icon: FeatherShield,
        to: "/dashboard/compliance",
        matchPaths: ["/dashboard/2b"],
      },
    ],
  },
  {
    id: "close",
    label: "CLOSE",
    items: [
      {
        id: "month-end-checklist",
        label: "Month-end Checklist",
        icon: FeatherClipboard,
        to: "/dashboard/checklist",
      },
    ],
  },
  {
    id: "reports",
    label: "REPORTS",
    items: [
      {
        id: "reports",
        label: "Reports",
        icon: FeatherPieChart,
        to: "/reports",
        // Live Zoho statements (ZOUR-22). Hidden on Tally tenants.
        backend: "zoho",
      },
      {
        id: "financials",
        label: "Financials (P&L, BS, AR, AP)",
        icon: FeatherBarChart2,
        to: "/books",
        // `/books/*` is declared clientOnly in App.tsx, so staff users are
        // redirected away from it. Hidden rather than shown-and-broken.
        audience: "clientViewOnly",
      },
      {
        id: "cfo-reports",
        label: "CFO Reports",
        icon: FeatherFileText,
        to: "/cfo",
        matchPaths: ["/cfo/dashboard"],
      },
    ],
  },
];

/**
 * Footer item. Points at the tenant settings area, which lands on its first
 * panel (Company details).
 *
 * Admin only, because every panel behind it is. Hidden rather than
 * shown-and-bouncing: a Settings link that always lands on /unauthorized is
 * worse than no link.
 */
export const NAV_FOOTER_ITEM: NavItemConfig = {
  id: "settings",
  label: "Settings",
  icon: FeatherSettings,
  to: "/settings",
  audience: "adminOnly",
};

/**
 * True when `pathname` should light up `item`.
 *
 * Exact match for Home, prefix match everywhere else, so `/dashboard/invoices/123`
 * still highlights Sales. Without the Home special case, `/v1` would prefix-match
 * every future `/v1/*` screen.
 */
export const isNavItemActive = (item: NavItemConfig, pathname: string): boolean => {
  const candidates = [item.to, ...(item.matchPaths ?? [])].filter(
    (path): path is string => Boolean(path)
  );

  return candidates.some((path) =>
    path === V1_HOME_PATH ? pathname === path : pathname.startsWith(path)
  );
};

/**
 * Filters out items the current user must not see.
 *
 * `isAdmin` defaults to false so a caller that has not been taught about the
 * flag hides admin items rather than showing them. Failing closed is the only
 * safe default for a visibility test.
 *
 * `accountingBackend` defaults via `matchesAccountingBackendGate` to the
 * stored company backend, so Zoho-only / Tally-only items stay off the wrong
 * tenant without every caller threading the value.
 */
export const isNavItemVisible = (
  item: NavItemConfig,
  isClientView: boolean,
  isAdmin: boolean = false,
): boolean => {
  if (!matchesAccountingBackendGate(item.backend)) {
    return false;
  }

  if (item.audience === "clientViewOnly") {
    return isClientView;
  }

  if (item.audience === "adminOnly") {
    return isAdmin;
  }

  return true;
};
